import {
  PARTNER_DEAL_STAGE_LABELS,
  PARTNER_VIEW_STAGE_LABELS,
  PARTNER_DEAL_STAGES,
  PartnerDealResponse,
  PartnerDealStage,
  partnerDealStagesFor,
} from '@interfaces/partners/responses/partner-deal.response';
import { partnerTermYearsLabel } from '@src/app/shared/utils/partner-term-years.utils';
import {
  PartnerDealRejectionReason,
  PartnerDealTimelineEntryResponse,
} from '@interfaces/partners/responses/partner-deal-timeline.response';

/**
 * Como a tela do deal **conta** o estado do deal: rótulos, cartão do próximo passo, situação,
 * origem e a legenda da linha do tempo.
 *
 * Extraído de `DealDetailComponent` em 25/09/2026, quando o componente passou das ~1.000 linhas
 * que o guia do frontend fixa como teto. O corte foi aqui de propósito: estas são as únicas
 * funções da tela que **não dependem de estado nem de injeção** — recebem o que precisam, devolvem
 * texto. Isso as torna testáveis sem `TestBed` e deixa o componente cuidando do que é dele
 * (carregar, orquestrar ação e reagir a evento).
 *
 * ⚠️ **Nada aqui decide permissão.** Quem pode avançar, decidir ou reabrir é resolvido no
 * componente a partir de flags do servidor, e chega até aqui como booleano pronto. Uma régua de
 * autorização recalculada neste arquivo seria uma segunda fonte para a mesma pergunta.
 */

/**
 * `TimeSpan` do .NET, no formato `c`: `[d.]hh:mm:ss[.fffffff]`.
 *
 * 🐞 O ponto aparece em **duas** posições, e é aí que a leitura ingênua quebra: antes do
 * primeiro `:` ele separa os DIAS, depois do último ele separa os décimos de SEGUNDO. Quebrar a
 * string no primeiro ponto funciona enquanto houver dias e falha silenciosamente quando não há —
 * `"23:51:39.6059474"` vira dias `NaN` e horas `6059474`, e a tela anuncia **"9504722 horas"**
 * (visto em 24/09/2026, no deal da SUZANO).
 *
 * Por isso o grupo dos dias exige o ponto **antes** dos dois-pontos, e a fração é descartada.
 */
const DURACAO = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/;

/**
 * O que a aba "Visão geral" mostra em cada etapa do funil.
 *
 * **Cada etapa tem o seu conteúdo**, e só o dela: o consentimento do cliente pertence à etapa de
 * Consentimento e não faz sentido em Cotação, do mesmo jeito que as propostas dos bancos não
 * fazem sentido em Registro. Exibir tudo em toda etapa transformaria a tela num formulário
 * comprido em que a pessoa procura o que interessa agora.
 *
 * ⚠️ As etapas ainda não implementadas anunciam **o que vai morar ali**, em vez de deixar a
 * coluna vazia. Vazio se lê como defeito; um texto que nomeia o que vem se lê como roadmap — e é
 * a mesma decisão já tomada ao deixar Parecer, Análise econômica e Cotações fora deste card.
 */
const CONTEUDO_POR_ETAPA: Record<PartnerDealStage, { titulo: string; texto: string }> = {
  // Registro e Consentimento têm tela própria (o dossiê da empresa, card 2986, e o termo) e não
  // usam este mapa; as entradas ficam para o Record continuar completo.
  Registro: {
    titulo: 'Aguardando a pré-aprovação da Legatus',
    texto:
      'O deal foi registrado e está na fila de triagem. Enquanto a Legatus não pré-aprovar, não há ação do parceiro — e o cliente ainda não é contactado.',
  },
  Consentimento: {
    titulo: 'Consentimento do cliente',
    texto: 'O NDA e a carta de autorização de SCR assinados pelo cliente e pela Legatus.',
  },
  Documentacao: {
    titulo: 'Documentação do cliente',
    texto:
      'O envio e a conferência dos documentos do cliente vão morar aqui: balanço, faturamento, contrato social e o que mais a análise exigir.',
  },
  Gates: {
    titulo: 'Gates de elegibilidade',
    texto:
      'As travas objetivas que o cliente precisa passar antes da análise — porte, setor, restrições e tempo de operação — com o resultado de cada uma.',
  },
  Analise: {
    titulo: 'Análise econômico-financeira',
    texto:
      'Os números do cliente lidos pela Legatus: capacidade de pagamento, endividamento e a leitura de risco que embasa a proposta.',
  },
  Comite: {
    titulo: 'Comitê de crédito',
    texto: 'A decisão do comitê, com o parecer e as condições aprovadas.',
  },
  Cotacao: {
    titulo: 'Cotação com os bancos',
    texto:
      'As propostas recebidas e a comparação entre elas — taxa, prazo, garantias e custo total de cada banco.',
  },
  Contratacao: {
    titulo: 'Contrato enviado',
    texto: 'A formalização com o banco escolhido: instrumento, assinaturas e liberação.',
  },
  Operacao: {
    titulo: 'Operação contratada',
    texto:
      'O acompanhamento do que foi contratado — desembolso, parcelas e o que a Legatus passa a monitorar.',
  },
};

/** A situação do deal em uma frase, com o tom que a pinta. */
export interface SituacaoDoDeal {
  label: string;
  tone: 'danger' | 'warning' | 'ok';
}

/** O cartão do alto da lateral: em que pé o deal está e o botão que resolve. */
export interface ProximoPasso {
  tom: 'ok' | 'aguardando' | 'rejeitado';
  eyebrow: string;
  titulo: string;
  texto: string;
  /** Linha secundária, em fonte menor. Hoje só a data da rejeição. */
  detalhe?: string | null;
  acao: 'avancar' | 'solicitar' | 'cobrar' | 'preaprovar' | 'reabrir' | null;
}

/**
 * Tudo que o {@link resolverProximoPasso} precisa saber para decidir o cartão.
 *
 * É um objeto e não sete parâmetros soltos porque a ordem de sete booleanos é impossível de
 * acertar na chamada — e trocar dois deles produz um cartão plausível e errado, que ninguém
 * percebe até o usuário reclamar.
 */
export interface ContextoDoProximoPasso {
  deal: PartnerDealResponse;
  /** Status do termo, quando existe. */
  statusDoTermo?: string | null;
  /** Se o termo já foi para o provedor e ainda não foi cancelado. */
  termoEmAndamento: boolean;
  /** Assinaturas que faltam no termo. */
  pendentesDeAssinatura: number;
  /** Se quem está vendo é do time interno (flag do servidor). */
  podeDecidir: boolean;
  /** Se quem está vendo move a etapa (flag do servidor). */
  podeAvancarEtapa: boolean;
  /** Se quem está vendo pode reabrir um deal rejeitado. */
  podeReabrir: boolean;
  /** Motivo e observação da rejeição vigente, quando há. */
  rejeicao: { motivo: string | null; observacao: string | null } | null;
  /** Data da rejeição já formatada — a formatação depende do perfil do usuário. */
  rejeitadoEm: string;
}

/**
 * O que a situação do deal diz, em uma frase.
 *
 * ⚠️ **Rejeitado vem antes da etapa.** A etapa continua apontando onde o deal parou — é o que
 * permite reabri-lo de lá —, então ler só ela faria um deal rejeitado parecer em andamento.
 *
 * @param deal Deal avaliado. Ausente devolve rótulo vazio.
 * @param internalView `true` para o time interno; o parceiro lê os rótulos dele (card 3612).
 */
export function resolverSituacao(
  deal: PartnerDealResponse | null,
  internalView = true
): SituacaoDoDeal {
  if (!deal) return { label: '', tone: 'ok' };

  if (deal.rejectedAt) return { label: 'Rejeitado', tone: 'danger' };

  switch (deal.stage) {
    case 'Registro':
      return { label: 'Aguardando pré-aprovação', tone: 'warning' };
    case 'Consentimento':
      return { label: 'Aguardando consentimento', tone: 'warning' };
    default:
      return { label: stageLabelsFor(internalView)[deal.stage], tone: 'ok' };
  }
}

/**
 * A pendência que depende de quem está olhando — a faixa "Depende de você".
 *
 * Existe porque **nada no fluxo bloqueia**: o parceiro pode subir documento com o NDA pendente.
 * Sem um aviso visível, a pendência não existiria para ninguém até alguém cobrar.
 *
 * @param deal Deal avaliado.
 * @param statusDoTermo Status do termo, quando existe.
 * @param termoEmAndamento Se o termo já foi para o provedor.
 */
export function resolverDependeDeVoce(
  deal: PartnerDealResponse | null,
  statusDoTermo: string | null | undefined,
  termoEmAndamento: boolean
): string | null {
  if (!deal || deal.rejectedAt) return null;

  if (deal.stage === 'Consentimento') {
    // ⚠️ A etapa sozinha não basta. O termo pode já estar ASSINADO e o deal continuar em
    // Consentimento até alguém movê-lo — mandar "reforçar a assinatura" nesse momento é
    // pedir o que já foi feito, e quem lê perde a confiança na faixa inteira.
    if (statusDoTermo === 'Assinado') return null;

    return termoEmAndamento
      ? 'Reforçar com o cliente a assinatura do NDA + carta SCR'
      : 'Enviar o NDA + carta SCR para assinatura do cliente';
  }

  return null;
}

/**
 * A etapa seguinte no funil, ou `null` quando o deal já está na última.
 *
 * @param atual Etapa atual do deal.
 * @param etapas Funil de quem está vendo — sem as etapas internas para o parceiro (card 3088), para
 *   a "próxima etapa" dele depois de Documentação ser Análise, e não Gates.
 */
export function resolverProximaEtapa(
  atual: PartnerDealStage | null | undefined,
  etapas: PartnerDealStage[] = PARTNER_DEAL_STAGES
): PartnerDealStage | null {
  if (!atual) return null;

  const seguinte = etapas[etapas.indexOf(atual) + 1];
  return seguinte ?? null;
}

/**
 * O cartão do alto da lateral: em que pé está o deal, e o botão que resolve.
 *
 * ⚠️ É o **único** lugar da tela que diz o que fazer agora, e por isso ele não pode inventar uma
 * ação quando não há uma — em metade dos estados a resposta honesta é "a bola não está com você".
 *
 * Os estados, na ordem em que são resolvidos:
 *
 * | Situação | O que o cartão diz | Ação |
 * |---|---|---|
 * | Rejeitado | o motivo e a observação da recusa | `reabrir` (só a Legatus) |
 * | Registro, visão Legatus | decidir se o deal entra no funil | `preaprovar` |
 * | Registro, visão parceiro | aguardando a pré-aprovação | — |
 * | Consentimento sem termo | enviar o termo | `solicitar` |
 * | Consentimento com termo pendente | quantas assinaturas faltam | `cobrar` |
 * | Última etapa | o funil acabou | — |
 * | Quem não move etapa | onde o deal está e com quem | — |
 * | Resto | a etapa seguinte | `avancar` |
 *
 * ⚠️ **Rejeitado vem primeiro**, antes de qualquer leitura de etapa: a etapa de um deal rejeitado
 * continua apontando onde ele parou — é o que permite reabri-lo de lá —, e lê-la antes faria um
 * deal recusado anunciar o próximo passo como se estivesse em andamento.
 *
 * @param ctx O estado do deal e as permissões já resolvidas pelo componente.
 */
export function resolverProximoPasso(ctx: ContextoDoProximoPasso): ProximoPasso | null {
  const { deal } = ctx;
  if (!deal) return null;

  if (deal.rejectedAt) {
    return {
      tom: 'rejeitado',
      eyebrow: 'Deal rejeitado',
      // O motivo vira o título porque é a primeira coisa que quem abre a tela quer saber. Sem
      // ele (deal rejeitado antes da linha do tempo existir) o cartão ainda aparece: esconder
      // o aviso por falta do detalhe seria trocar "recusado, motivo desconhecido" por
      // "aparentemente em andamento", que é pior.
      titulo: ctx.rejeicao?.motivo ?? 'Sem motivo registrado',
      texto:
        ctx.rejeicao?.observacao ??
        'A Legatus recusou este deal. A etapa em que ele parou foi preservada — reabrir o traz de volta exatamente de lá.',
      detalhe: `Rejeitado em ${ctx.rejeitadoEm}`,
      acao: ctx.podeReabrir ? 'reabrir' : null,
    };
  }

  // Registro só sai pela PRÉ-APROVAÇÃO, e por isso é tratado antes da régua de avançar etapa:
  // a decisão dispara e-mail para o parceiro e escolhe o destino (pula o Consentimento quando o
  // cliente já assinou o termo). Um "Avançar etapa" aqui faria o mesmo trajeto sem nada disso.
  if (deal.stage === 'Registro') {
    if (ctx.podeDecidir) {
      return {
        tom: 'aguardando',
        eyebrow: 'Depende de você',
        titulo: 'Pré-aprovar este deal',
        texto:
          'O deal está na fila de triagem. Pré-aprovar o coloca no funil e avisa o parceiro por e-mail; rejeitar encerra com um motivo.',
        acao: 'preaprovar',
      };
    }

    return {
      tom: 'aguardando',
      eyebrow: 'Aguardando',
      titulo: 'Pré-aprovação da Legatus',
      texto:
        'O deal foi registrado e está na fila de triagem. Não há ação sua por enquanto — quando a Legatus pré-aprovar, ele avança sozinho e você é avisado por e-mail.',
      acao: null,
    };
  }

  if (deal.stage === 'Consentimento' && ctx.statusDoTermo !== 'Assinado') {
    if (!ctx.termoEmAndamento) {
      return {
        tom: 'aguardando',
        eyebrow: 'Depende de você',
        titulo: 'Enviar o termo para assinatura',
        texto:
          'O NDA e a carta de autorização de SCR ainda não foram enviados. O deal segue parado até o cliente assinar.',
        acao: 'solicitar',
      };
    }

    const pendentes = ctx.pendentesDeAssinatura;

    return {
      tom: 'aguardando',
      eyebrow: 'Aguardando',
      titulo: pendentes === 1 ? '1 assinatura pendente' : `${pendentes} assinaturas pendentes`,
      texto:
        'O termo já foi enviado. Enquanto faltar assinatura, o deal não avança — dá para reforçar o convite por e-mail.',
      acao: pendentes > 0 ? 'cobrar' : null,
    };
  }

  const seguinte = resolverProximaEtapa(deal.stage, partnerDealStagesFor(ctx.podeDecidir));
  // O parceiro lê os rótulos dele ("NDA e Bacen", "Contrato enviado", card 3612); `podeDecidir` é a
  // visão interna.
  const labels = stageLabelsFor(ctx.podeDecidir);
  if (!seguinte) {
    return {
      tom: 'ok',
      eyebrow: 'Última etapa',
      titulo: labels[deal.stage],
      texto: 'O deal percorreu o funil inteiro.',
      acao: null,
    };
  }

  // Sem o botão, "Próximo passo: Documentação" viraria uma instrução sem como cumprir. Para
  // quem não move o deal, o cartão passa a responder outra pergunta — onde ele está e com
  // quem —, que é a que sobra quando não há ação.
  if (!ctx.podeAvancarEtapa) {
    return {
      tom: 'ok',
      eyebrow: 'Em andamento',
      titulo: labels[deal.stage],
      texto:
        deal.stage === 'Consentimento'
          ? `${labels.Consentimento} assinado pelas duas partes. A Legatus assume daqui: o avanço para ${labels[seguinte]} é do time interno.`
          : 'O deal está com a Legatus. Quem avança as etapas do funil é o time interno — você acompanha o andamento por aqui.',
      acao: null,
    };
  }

  return {
    tom: 'ok',
    eyebrow: 'Próximo passo',
    titulo: labels[seguinte],
    texto:
      deal.stage === 'Consentimento'
        ? `${labels.Consentimento} assinado pelas duas partes. Avance para seguir com a documentação do cliente.`
        : `O deal está em ${labels[deal.stage]}. Avance quando esta etapa estiver cumprida.`,
    acao: 'avancar',
  };
}

/** Rótulos das etapas para quem está vendo: interno ou parceiro ("NDA e Bacen", "Contrato enviado"). */
function stageLabelsFor(internalView: boolean): Record<PartnerDealStage, string> {
  return internalView ? PARTNER_DEAL_STAGE_LABELS : PARTNER_VIEW_STAGE_LABELS;
}

/**
 * De quem é o deal, para o cabeçalho.
 *
 * Na visão da **Legatus** vem o nome do parceiro: o board dela mistura vários, e "originado pelo
 * parceiro" não distingue nada. Na visão do **parceiro** vem quem registrou — lá o nome do
 * parceiro seria sempre o mesmo, mas o master enxerga os deals de toda a equipe (card 2929).
 *
 * ⚠️ **Nada aqui depende da etapa.** Mover o deal de coluna não muda origem nem autoria — o
 * `UpdateStageAsync` toca só o `Stage`. Se o rótulo parecer ter mudado depois de um arrasto, o que
 * mudou foi a VISÃO (interna × parceiro), não o dado.
 *
 * @param deal Deal avaliado.
 */
export function resolverOrigemDoDeal(deal: PartnerDealResponse | null): string {
  if (!deal) return '';

  if (!deal.partnerId) return 'Deal da Legatus';

  const parceiro = deal.partnerName?.trim();
  const autor = deal.registeredByName?.trim();

  // ⚠️ O `origin` NÃO entra aqui. Este campo responde "de quem é o deal", e o tipo dele —
  // registrado pelo parceiro ou vinculado pela Legatus — já está em **Situação do deal ›
  // Origem**, logo abaixo. Repetir "Deal manual" no cabeçalho gastava o espaço do nome para
  // dizer duas vezes a mesma coisa, e no fim deixava o rótulo mais pobre justamente no caso
  // em que o nome mais importa.
  if (parceiro) return `Originado por ${parceiro}`;
  if (autor) return `Registrado por ${autor}`;

  return 'Originado pelo parceiro';
}

/** Prazo da captação no cabeçalho do deal (card 3683). */
export interface DealTermSummary {
  /** "3 anos", ou "—" no deal antigo, sem prazo. */
  label: string;
  /** Flat fee estimado, calculado no backend (`PartnerDealDto`, card 3707). Nulo sem prazo. */
  estimate: { flatFeePercent: number; flatFeeAmount: number } | null;
}

/**
 * Prazo da captação e o flat fee estimado, para o cabeçalho da tela do deal (card 3683). O flat fee
 * vem pronto do backend (`flatFeePercent` e `estimatedFlatFeeAmount`, card 3707): a tela só mostra.
 *
 * @param deal Deal avaliado.
 */
export function resolveDealTerm(deal: PartnerDealResponse | null): DealTermSummary {
  const flatFeePercent = deal?.flatFeePercent ?? null;
  const flatFeeAmount = deal?.estimatedFlatFeeAmount ?? null;
  return {
    label: partnerTermYearsLabel(deal?.termYears ?? null),
    estimate:
      flatFeePercent !== null && flatFeeAmount !== null ? { flatFeePercent, flatFeeAmount } : null,
  };
}

/**
 * O que a etapa apresenta na Visão geral quando ela ainda não tem tela própria.
 *
 * @param stage Etapa exibida. Nula devolve `null`.
 */
export function conteudoDaEtapa(
  stage: PartnerDealStage | null,
  internalView = true
): { titulo: string; texto: string } | null {
  if (!stage) return null;
  const content = CONTEUDO_POR_ETAPA[stage];
  // O parceiro não lê "Consentimento" nem "Contratação" (card 3612): o título vira o rótulo dele.
  if (!internalView && (stage === 'Consentimento' || stage === 'Contratacao')) {
    return { ...content, titulo: PARTNER_VIEW_STAGE_LABELS[stage] };
  }
  return content;
}

/**
 * Texto do acontecimento na linha do tempo.
 *
 * @param entrada Entrada da linha do tempo.
 * @param rotulosDeMotivo Rótulos dos motivos de rejeição.
 * @param internalView `true` para o time interno; o parceiro lê os rótulos dele (card 3612).
 */
export function descreverEvento(
  entrada: PartnerDealTimelineEntryResponse,
  rotulosDeMotivo: Record<PartnerDealRejectionReason, string>,
  internalView = true
): string {
  const labels = stageLabelsFor(internalView);
  switch (entrada.eventType) {
    case 'PreApproved':
      return 'Deal pré-aprovado pela Legatus';
    case 'Rejected':
      return entrada.rejectionReason
        ? `Deal rejeitado — ${rotulosDeMotivo[entrada.rejectionReason]}`
        : 'Deal rejeitado';
    case 'RejectionReverted':
      return 'Rejeição revertida — deal reaberto';
    case 'ClientTermSigned':
      return 'NDA + SCR assinado pelo cliente — avanço automático';
    case 'ClientAccessInvited':
      return entrada.notes
        ? `Acesso do cliente enviado para ${entrada.notes}`
        : 'Acesso do cliente enviado';
    case 'BankKitRequested':
      // Os e-mails que receberam vêm em `notes`, que a linha do tempo já mostra logo abaixo do título.
      return 'Kit Banco solicitado ao cliente';
    case 'StageChanged':
      return entrada.fromStage
        ? `${labels[entrada.fromStage]} → ${labels[entrada.toStage!]}`
        : `Deal registrado em ${labels[entrada.toStage!]}`;
    default:
      return '';
  }
}

/**
 * Ícone do acontecimento na linha do tempo.
 *
 * O ícone carrega o que o texto já diz — serve para varrer a coluna com o olho e achar a
 * rejeição no meio de dez mudanças de etapa, sem ler cada linha.
 *
 * @param entrada Entrada da linha do tempo.
 */
export function iconeDoEvento(entrada: PartnerDealTimelineEntryResponse): string {
  switch (entrada.eventType) {
    case 'PreApproved':
      return 'pi-verified';
    case 'Rejected':
      return 'pi-times-circle';
    case 'RejectionReverted':
      return 'pi-replay';
    case 'ClientTermSigned':
      return 'pi-file-check';
    case 'ClientAccessInvited':
      return 'pi-user-plus';
    case 'BankKitRequested':
      return 'pi-envelope';
    case 'StageChanged':
      return entrada.fromStage ? 'pi-arrow-right' : 'pi-flag';
    default:
      return 'pi-circle-fill';
  }
}

/**
 * Converte a duração do .NET (`d.hh:mm:ss` ou `hh:mm:ss`) em algo legível.
 *
 * O backend manda `TimeSpan`, que o JSON serializa como string. Formatar aqui — e não lá —
 * porque "3 dias" e "3 d" são decisões de tela, não de domínio.
 *
 * @param valor Duração como o backend a serializa.
 */
export function formatarDuracao(valor?: string | null): string {
  if (!valor) return '—';

  const partes = DURACAO.exec(valor.trim());
  if (!partes) return '—';

  const dias = Number(partes[1] ?? 0);
  const horas = Number(partes[2] ?? 0);

  if (dias >= 1) return dias === 1 ? '1 dia' : `${dias} dias`;
  if (horas >= 1) return horas === 1 ? '1 hora' : `${horas} horas`;
  return 'menos de 1 hora';
}
